// src/components/DpcStepChart.tsx
// DPC 包括点数 階段チャート (native SVG)。 ふくろ君風の独自実装。
//
// 入力: days_I/II/III (= 期間 境界日)、 points_I/II/III (= 1 日あたり点数)、
//       coefficient (= 医療機関別係数)、 days (= 想定入院日数)。
// 表示: 期間 I (= 1〜days_I) / II (= days_I+1〜days_II) / III (= days_II+1〜days_III)
//       で 1 日点数が段階的に下がる。 入院期間 III 超は 出来高算定 (chart 上は III の値を継続)。
//
// 軸: X = 入院日数 (1〜maxDay)、 Y = 1 日あたり包括金額 (= 点数 × 係数 × 10 円)。
// 縦線: 期間 I/II 境界 / II/III 境界 / 想定入院日数 (= ユーザ slider)。

interface Props {
  days_I:     number;
  days_II:    number;
  days_III:   number;
  points_I:   number;
  points_II:  number;
  points_III: number;
  // ユーザ入力
  days:        number;   // 想定入院日数 (= 1〜60)
  coefficient: number;   // 医療機関別係数
}

export function DpcStepChart(p: Props) {
  // 1 点 = 10 円、 医療機関別係数で補正 → 1 日あたり包括金額に換算してプロット
  const yenMul = p.coefficient * 10;
  const yen_I   = p.points_I   * yenMul;
  const yen_II  = p.points_II  * yenMul;
  const yen_III = p.points_III * yenMul;

  // 円表記 helper (= 1 万円超は「○.○万円」 で短縮、 軸ラベル詰まり対策)
  const formatYen = (y: number): string => {
    if (y < 10000) return `${Math.round(y).toLocaleString()}円`;
    return `${(y / 10000).toFixed(1)}万円`;
  };

  // 描画範囲
  const maxDay = Math.max(p.days_III + 5, p.days + 2, 20);
  const maxYen = Math.max(yen_I, yen_II, yen_III) * 1.15;

  // SVG 座標系 (2026-05-28 ユーザ指示で縦幅 1/3 に縮小: H 320→110)
  const W = 720, H = 110;
  const pad = { top: 10, right: 16, bottom: 22, left: 62 };  // 円表記で字幅増 → left 50→62
  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;

  const xToPx = (x: number) => pad.left + (x / maxDay) * innerW;
  const yToPx = (y: number) => pad.top + innerH - (y / maxYen) * innerH;

  // 階段 path:
  //   day 1 (= x=0) → day=days_I で yen_I
  //   day=days_I+1 → day=days_II で yen_II
  //   day=days_II+1 → day=days_III で yen_III
  //   day=days_III+1 以降 (= 出来高) は 0 (= 包括対象外を 0 表示)
  // 「日次の階段」 を 横線 + 縦線 で表現。
  const steps: { x1: number; x2: number; y: number; label: string; color: string }[] = [
    { x1: 0,             x2: p.days_I,   y: yen_I,   label: "I",   color: "#10b981" },
    { x1: p.days_I,      x2: p.days_II,  y: yen_II,  label: "II",  color: "#f59e0b" },
    { x1: p.days_II,     x2: p.days_III, y: yen_III, label: "III", color: "#ef4444" },
  ];

  // Y 軸 ticks: 円単位、 1000 円単位で丸め (= maxYen が数万円のため 100 単位だと細かすぎ)
  const rawYTicks = [0, 0.25, 0.5, 0.75, 1].map((r) => Math.round(maxYen * r / 1000) * 1000);
  const yTicks: number[] = [];
  let lastYpx = -Infinity;
  for (const y of rawYTicks) {
    const px = yToPx(y);
    if (Math.abs(px - lastYpx) >= 24) {
      yTicks.push(y);
      lastYpx = px;
    }
  }

  // X 軸 ticks: ユーザ指示で 0 + 期間区切り (days_I, days_II, days_III) のみ表示。
  // 等しい値は重複削除。
  const xTicks: number[] = Array.from(
    new Set([0, p.days_I, p.days_II, p.days_III].filter(v => v >= 0 && v <= maxDay))
  ).sort((a, b) => a - b);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="border border-slate-200 rounded bg-white">
      {/* Y grid + labels */}
      {yTicks.map((y, i) => (
        <g key={`y-${i}`}>
          <line x1={pad.left} x2={W - pad.right} y1={yToPx(y)} y2={yToPx(y)}
                stroke="#e2e8f0" strokeDasharray="2 2" />
          <text x={pad.left - 6} y={yToPx(y) + 4} fontSize={10} textAnchor="end" fill="#64748b">
            {formatYen(y)}
          </text>
        </g>
      ))}

      {/* X ticks */}
      {xTicks.map((x, i) => (
        <g key={`x-${i}`}>
          <line x1={xToPx(x)} x2={xToPx(x)} y1={pad.top + innerH} y2={pad.top + innerH + 4}
                stroke="#94a3b8" />
          <text x={xToPx(x)} y={pad.top + innerH + 18} fontSize={10} textAnchor="middle" fill="#64748b">
            {x}
          </text>
        </g>
      ))}
      <text x={W / 2} y={H - 6} fontSize={11} textAnchor="middle" fill="#475569">入院日数</text>
      {/* 2026-05-28: y 軸ラベル「点数/日」 削除 (= ユーザ指示)。 凡例で代替。 */}

      {/* axis lines */}
      <line x1={pad.left} x2={pad.left} y1={pad.top} y2={pad.top + innerH} stroke="#475569" />
      <line x1={pad.left} x2={W - pad.right} y1={pad.top + innerH} y2={pad.top + innerH} stroke="#475569" />

      {/* steps: 期間 I/II/III の塗り (= 棒の代わりに矩形 + 上辺の太線) */}
      {steps.map((s, i) => {
        const x1 = xToPx(s.x1), x2 = xToPx(s.x2);
        const y  = yToPx(s.y);
        const y0 = yToPx(0);
        return (
          <g key={`s-${i}`}>
            <rect x={x1} y={y} width={x2 - x1} height={y0 - y}
                  fill={s.color} fillOpacity={0.18} />
            <line x1={x1} x2={x2} y1={y} y2={y} stroke={s.color} strokeWidth={2.5} />
            {/* 期間境界 (右端) の縦線 (= III 末端を除く) */}
            {i < 2 && (
              <line x1={x2} x2={x2} y1={pad.top} y2={pad.top + innerH}
                    stroke={s.color} strokeDasharray="3 3" strokeOpacity={0.6} />
            )}
            {/* ラベル: 点数のみ (= 「I/II/III」 は凡例に移動、 ユーザ指示) */}
            <text x={(x1 + x2) / 2} y={y - 6} fontSize={11} textAnchor="middle"
                  fill={s.color} fontWeight={600}>
              {formatYen(s.y)}
            </text>
          </g>
        );
      })}

      {/* 凡例 (= 右上、 期間 I/II/III と色対応) */}
      <g transform={`translate(${W - pad.right - 116}, ${pad.top + 4})`}>
        {steps.map((s, i) => (
          <g key={`legend-${i}`} transform={`translate(0, ${i * 14})`}>
            <rect x={0} y={-8} width={12} height={10} fill={s.color} fillOpacity={0.6} />
            <text x={16} y={1} fontSize={10} fill="#334155">
              期間 {s.label} (1日 {formatYen(s.y)})
            </text>
          </g>
        ))}
      </g>

      {/* 想定入院日数の縦線 (2026-05-28: ラベルを線の右側内 に配置 = 上はみ出し対策) */}
      {p.days >= 0 && p.days <= maxDay && (
        <g>
          <line x1={xToPx(p.days)} x2={xToPx(p.days)} y1={pad.top} y2={pad.top + innerH}
                stroke="#2563eb" strokeWidth={2} />
          <text x={xToPx(p.days) + 4} y={pad.top + 10} fontSize={11} textAnchor="start"
                fill="#2563eb" fontWeight={600}>
            想定 {p.days}日
          </text>
        </g>
      )}
    </svg>
  );
}
